// Simplex noise implementation for deterministic organic coastlines
// Adapted from classic public domain implementations

const grad3 = new Int8Array([
    1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0,
    1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, -1,
    0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1
]);

const p = new Uint8Array(256);
for (let i = 0; i < 256; i++) p[i] = i;

// Seedable shuffle (Fisher-Yates) for determinism
export function seedNoise(seed: number) {
    let internalSeed = seed;
    const random = () => {
        const x = Math.sin(internalSeed++) * 10000;
        return x - Math.floor(x);
    };
    for (let i = 255; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [p[i], p[j]] = [p[j], p[i]];
    }
}

// Initial seed
seedNoise(12345);

const perm = new Uint8Array(512);
const gradP = new Uint8Array(512);

function updatePerm() {
    for (let i = 0; i < 512; i++) {
        perm[i] = p[i & 255];
        gradP[i] = perm[i] % 12;
    }
}
updatePerm();

export function noise2D(xin: number, yin: number): number {
    let n0, n1, n2;
    const F2 = 0.5 * (Math.sqrt(3.0) - 1.0);
    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const G2 = (3.0 - Math.sqrt(3.0)) / 6.0;
    const t = (i + j) * G2;
    const x0 = xin - i + t;
    const y0 = yin - j + t;
    let i1, j1;
    if (x0 > y0) { i1 = 1; j1 = 0; }
    else { i1 = 0; j1 = 1; }
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1.0 + 2.0 * G2;
    const y2 = y0 - 1.0 + 2.0 * G2;
    const ii = i & 255;
    const jj = j & 255;

    const t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 < 0) n0 = 0.0;
    else {
        const gi0 = gradP[ii + perm[jj]] * 3;
        n0 = t0 * t0 * t0 * t0 * (grad3[gi0] * x0 + grad3[gi0 + 1] * y0);
    }

    const t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 < 0) n1 = 0.0;
    else {
        const gi1 = gradP[ii + i1 + perm[jj + j1]] * 3;
        n1 = t1 * t1 * t1 * t1 * (grad3[gi1] * x1 + grad3[gi1 + 1] * y1);
    }

    const t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 < 0) n2 = 0.0;
    else {
        const gi2 = gradP[ii + 1 + perm[jj + 1]] * 3;
        n2 = t2 * t2 * t2 * t2 * (grad3[gi2] * x2 + grad3[gi2 + 1] * y2);
    }
    return 70.0 * (n0 + n1 + n2);
}
